import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Sparkles, RefreshCw, Share2 } from 'lucide-react';
import ShareButton from '../components/ShareButton';

export default function Affirmations() {
  const { currentColors } = useTheme();
  const [affirmation, setAffirmation] = useState(null);
  const [allAffirmations, setAllAffirmations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAffirmations();
    loadRandomAffirmation();
  }, []);

  const loadAffirmations = async () => {
    try {
      const response = await axios.get(`${API}/affirmations`);
      setAllAffirmations(response.data);
    } catch (error) {
      console.error('Failed to load affirmations:', error);
    }
  };

  const loadRandomAffirmation = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/affirmations/random`);
      setAffirmation(response.data);
    } catch (error) {
      console.error('Failed to load affirmation:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedAffirmations = allAffirmations.reduce((acc, aff) => {
    if (!acc[aff.category]) acc[aff.category] = [];
    acc[aff.category].push(aff);
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="affirmations-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Daily Affirmations</h1>
        <p className="text-gray-600">Positive thoughts for a positive life</p>
      </div>

      {/* Featured Affirmation */}
      {affirmation && (
        <div className="bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl p-12 text-white shadow-2xl relative" data-testid="featured-affirmation">
          {/* Share Button */}
          <div className="absolute top-4 right-4">
            <ShareButton
              title={`"${affirmation.text}"`}
              description={`Daily affirmation for ${affirmation.category} - The Aura Space`}
              imageUrl={affirmation.background_image}
              contentType="affirmation"
              contentId={affirmation.id}
              variant="card"
              className="!bg-white/20 hover:!bg-white/30"
            />
          </div>
          <div className="flex items-center justify-center mb-6">
            <Sparkles className="w-12 h-12" />
          </div>
          <p className="text-3xl md:text-4xl font-bold text-center mb-6 leading-relaxed">
            "{affirmation.text}"
          </p>
          <div className="flex items-center justify-center space-x-4">
            <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-medium">
              {affirmation.category}
            </span>
            <button
              onClick={loadRandomAffirmation}
              disabled={loading}
              className="px-6 py-2 bg-white text-purple-600 rounded-full font-medium hover:bg-purple-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>New Affirmation</span>
            </button>
          </div>
        </div>
      )}

      {/* All Affirmations by Category */}
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">All Affirmations</h2>
        {Object.entries(groupedAffirmations).map(([category, affs]) => (
          <div key={category} className="bg-white rounded-xl p-6 shadow-lg border border-amber-100">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <span>{category}</span>
              <span className="text-sm text-gray-500 font-normal">({affs.length})</span>
            </h3>
            <div className="space-y-3">
              {affs.map((aff) => (
                <div
                  key={aff.id}
                  className="p-4 bg-purple-50 rounded-lg border border-amber-100 hover:bg-purple-100 transition-colors flex items-center justify-between"
                  data-testid={`affirmation-${aff.id}`}
                >
                  <p className="text-gray-700 italic flex-1">"{aff.text}"</p>
                  <ShareButton
                    title={`"${aff.text}"`}
                    description={`Daily affirmation for ${aff.category} - The Aura Space`}
                    imageUrl={aff.background_image}
                    contentType="affirmation"
                    contentId={aff.id}
                    variant="icon"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
